// EQ Eight's curve, computed: each band as the biquads the browser builds
// for it (device-graph.ts), and their magnitude at any frequency — the line
// the EQ editor draws is the response the preview plays, not a sketch.
//
// The biquads follow the Web Audio spec's formulas (the Audio EQ Cookbook,
// with lowpass/highpass Q in dB and shelves at slope 1), so `bandFilters`
// can configure BiquadFilterNodes directly.

import type { EqBand, EqBandType } from "@web-shared/lib/device-params";

export interface BiquadSpec {
  type: BiquadFilterType;
  frequency: number;
  /** As a BiquadFilterNode reads it: dB for lowpass/highpass, linear otherwise. */
  Q: number;
  gain: number;
}

const BUTTERWORTH_Q = Math.SQRT1_2;

function cut(type: "lowpass" | "highpass", freq: number, q: number, steep: boolean): BiquadSpec[] {
  // 48 dB/oct: four sections, the band's resonance on the last.
  const qDb = (lin: number): number => 20 * Math.log10(Math.max(0.05, lin));
  if (!steep) return [{ type, frequency: freq, Q: qDb(q), gain: 0 }];
  return [
    { type, frequency: freq, Q: qDb(BUTTERWORTH_Q), gain: 0 },
    { type, frequency: freq, Q: qDb(BUTTERWORTH_Q), gain: 0 },
    { type, frequency: freq, Q: qDb(BUTTERWORTH_Q), gain: 0 },
    { type, frequency: freq, Q: qDb(q), gain: 0 },
  ];
}

/** The biquads one band stands for (none when it's off). */
export function bandFilters(b: Pick<EqBand, "on" | "type" | "freq" | "gain" | "q">): BiquadSpec[] {
  if (!b.on) return [];
  return filtersFor(b.type, b.freq, b.gain, b.q);
}

function filtersFor(type: EqBandType, freq: number, gain: number, q: number): BiquadSpec[] {
  switch (type) {
    case "lowcut48":
      return cut("highpass", freq, q, true);
    case "lowcut12":
      return cut("highpass", freq, q, false);
    case "highcut12":
      return cut("lowpass", freq, q, false);
    case "highcut48":
      return cut("lowpass", freq, q, true);
    case "lowshelf":
      return [{ type: "lowshelf", frequency: freq, Q: 1, gain }];
    case "highshelf":
      return [{ type: "highshelf", frequency: freq, Q: 1, gain }];
    case "bell":
      return [{ type: "peaking", frequency: freq, Q: q, gain }];
    case "notch":
      return [{ type: "notch", frequency: freq, Q: q, gain: 0 }];
  }
}

interface Coeffs {
  b0: number;
  b1: number;
  b2: number;
  a0: number;
  a1: number;
  a2: number;
}

/** The Web Audio spec's biquad coefficients. */
export function coefficients(s: BiquadSpec, sampleRate: number): Coeffs {
  const f = Math.max(1, Math.min(sampleRate / 2 - 1, s.frequency));
  const w0 = (2 * Math.PI * f) / sampleRate;
  const cos = Math.cos(w0);
  const sin = Math.sin(w0);
  const A = Math.pow(10, s.gain / 40);
  switch (s.type) {
    case "lowpass": {
      const alpha = sin / (2 * Math.pow(10, s.Q / 20));
      return { b0: (1 - cos) / 2, b1: 1 - cos, b2: (1 - cos) / 2, a0: 1 + alpha, a1: -2 * cos, a2: 1 - alpha };
    }
    case "highpass": {
      const alpha = sin / (2 * Math.pow(10, s.Q / 20));
      return { b0: (1 + cos) / 2, b1: -(1 + cos), b2: (1 + cos) / 2, a0: 1 + alpha, a1: -2 * cos, a2: 1 - alpha };
    }
    case "peaking": {
      const alpha = sin / (2 * Math.max(1e-4, s.Q));
      return { b0: 1 + alpha * A, b1: -2 * cos, b2: 1 - alpha * A, a0: 1 + alpha / A, a1: -2 * cos, a2: 1 - alpha / A };
    }
    case "notch": {
      const alpha = sin / (2 * Math.max(1e-4, s.Q));
      return { b0: 1, b1: -2 * cos, b2: 1, a0: 1 + alpha, a1: -2 * cos, a2: 1 - alpha };
    }
    case "bandpass": {
      const alpha = sin / (2 * Math.max(1e-4, s.Q));
      return { b0: alpha, b1: 0, b2: -alpha, a0: 1 + alpha, a1: -2 * cos, a2: 1 - alpha };
    }
    case "lowshelf": {
      const alpha = (sin / 2) * Math.SQRT2;
      const k = 2 * Math.sqrt(A) * alpha;
      return {
        b0: A * (A + 1 - (A - 1) * cos + k),
        b1: 2 * A * (A - 1 - (A + 1) * cos),
        b2: A * (A + 1 - (A - 1) * cos - k),
        a0: A + 1 + (A - 1) * cos + k,
        a1: -2 * (A - 1 + (A + 1) * cos),
        a2: A + 1 + (A - 1) * cos - k,
      };
    }
    case "highshelf": {
      const alpha = (sin / 2) * Math.SQRT2;
      const k = 2 * Math.sqrt(A) * alpha;
      return {
        b0: A * (A + 1 + (A - 1) * cos + k),
        b1: -2 * A * (A - 1 + (A + 1) * cos),
        b2: A * (A + 1 + (A - 1) * cos - k),
        a0: A + 1 - (A - 1) * cos + k,
        a1: 2 * (A - 1 - (A + 1) * cos),
        a2: A + 1 - (A - 1) * cos - k,
      };
    }
    default:
      return { b0: 1, b1: 0, b2: 0, a0: 1, a1: 0, a2: 0 };
  }
}

/** |H| of one biquad at `freq`. */
export function magnitude(s: BiquadSpec, freq: number, sampleRate = 48000): number {
  const c = coefficients(s, sampleRate);
  const w = (2 * Math.PI * freq) / sampleRate;
  const cos1 = Math.cos(w);
  const sin1 = Math.sin(w);
  const cos2 = Math.cos(2 * w);
  const sin2 = Math.sin(2 * w);
  const nr = c.b0 + c.b1 * cos1 + c.b2 * cos2;
  const ni = -(c.b1 * sin1 + c.b2 * sin2);
  const dr = c.a0 + c.a1 * cos1 + c.a2 * cos2;
  const di = -(c.a1 * sin1 + c.a2 * sin2);
  return Math.sqrt((nr * nr + ni * ni) / Math.max(1e-30, dr * dr + di * di));
}

/** The response in dB of a stack of biquads at `freq`. */
export function responseDb(filters: readonly BiquadSpec[], freq: number, sampleRate = 48000): number {
  let m = 1;
  for (const f of filters) m *= magnitude(f, freq, sampleRate);
  return 20 * Math.log10(Math.max(1e-9, m));
}
